import { useEffect, useMemo, useState } from 'react';
import {
  FiLayers,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiMapPin,
  FiUpload,
  FiDownload,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  PositionForm,
  Select,
  MultiSelect,
  PositionImportModal,
  TableActions,
} from '../../components';
import '../CompanyManager/style.css';

export default function PositionManager() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stationFilter, setStationFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [stations, setStations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Company management
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  const navigate = useNavigate();
  const permissions = usePermission('management');

  useEffect(() => {
    if (permissions.canView) {
      loadCompanies();
    }
  }, [permissions.canView]);

  async function loadCompanies() {
     try {
       const { data } = await Api.get('/companies');
       setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  // Effect to load positions when companies or companyFilter changes
  useEffect(() => {
    if (companies.length > 0) {
      // Validate if the selected company filter still exists
      if (companyFilter !== 'all' && !companies.find(c => String(c.id) === String(companyFilter))) {
        setCompanyFilter('all');
      } else {
        loadPositions();
      }
    }
  }, [companies, companyFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, stationFilter, sortOrder, companyFilter]);

  async function loadPositions() {
    try {
      setLoading(true);
      let data = [];
      let allStations = [];

      if (companyFilter === 'all') {
        const promises = companies.map(c => 
          Promise.all([
            Api.getPositions(c.id).catch(err => {
                 console.error(`Error loading positions for company ${c.id}:`, err);
                 return [];
            }),
            Api.getServiceStations(c.id).catch(() => [])
          ]).then(([pos, sts]) => {
              const positionsWithCompany = (pos || []).map(p => ({ ...p, company: c }));
              return { positions: positionsWithCompany, stations: sts };
          })
        );
        
        const results = await Promise.all(promises);
        data = results.flatMap(r => r.positions);
        allStations = results.flatMap(r => r.stations);
      } else {
        const selectedComp = companies.find(c => String(c.id) === String(companyFilter));
        if (selectedComp) {
            const [pos, sts] = await Promise.all([
                Api.getPositions(selectedComp.id),
                Api.getServiceStations(selectedComp.id)
            ]);
            data = (pos || []).map(p => ({ ...p, company: selectedComp }));
            allStations = sts || [];
        }
      }

      setPositions(data);
      setStations(allStations);
      setHasLoadedOnce(true);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  const handleExport = () => {
    if (!positions || positions.length === 0) {
        toast.warning('Não há dados para exportar.');
        return;
    }

    const exportData = positions.map(p => ({
        'Empresa': p.company ? (p.company.trade_name || p.company.corporate_name) : '',
        'Nome': p.name,
        'CBO': p.cbo || '',
        'Descrição': p.description || '',
        'Vínculo': p.serviceStations && p.serviceStations.length > 0 
            ? p.serviceStations.map(s => s.nickname || s.name).join(', ')
            : (p.service_station_id ? `ID: ${p.service_station_id}` : 'Geral'),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cargos");
    XLSX.writeFile(wb, "cargos.xlsx");
  };

  function handleCreate() {
    setSelectedPosition(null);
    setIsModalOpen(true);
  }

  function handleEdit(position) {
    setSelectedPosition(position);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadPositions();
    setIsModalOpen(false);
    setSelectedPosition(null);
  }

  function handleRequestDelete(position) {
    setPositionToDelete(position);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!positionToDelete) return;

    try {
      setIsDeleting(true);
      // Use the company from the position itself if available (aggregation mode), 
      // otherwise fall back to selected company or context.
      const targetCompanyId = positionToDelete.company?.id || (companyFilter !== 'all' ? companyFilter : null) || positionToDelete.company_id;
      
      if (!targetCompanyId) {
        alert('Erro: Empresa não identificada para exclusão.');
        return;
      }

      const result = await Api.deletePosition(targetCompanyId, positionToDelete.id);

      if (result && !result.error) {
        setPositions(prev => prev.filter(p => p.id !== positionToDelete.id));
        setIsDeleteModalOpen(false);
        setPositionToDelete(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao excluir cargo.',
        );
      }
    } catch (err) {
      alert('Erro ao excluir cargo.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setPositionToDelete(null);
  }

  const filteredPositions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return positions.filter(position => {
      const matchesSearch =
        !term ||
        (position.name || '').toLowerCase().includes(term) ||
        (position.description || '').toLowerCase().includes(term) ||
        (position.cbo || '').toLowerCase().includes(term);

      let matchesStation = true;

      if (stationFilter !== 'all') {
        if (stationFilter === 'general') {
            const hasLinked = position.serviceStations && position.serviceStations.length > 0;
            const hasLegacy = !!position.service_station_id;
            matchesStation = !hasLinked && !hasLegacy;
        } else {
            const hasLinked = position.serviceStations?.some(s => String(s.id) === String(stationFilter));
            const hasLegacy = String(position.service_station_id) === String(stationFilter);
            matchesStation = hasLinked || hasLegacy;
        }
      }

      const companyId = position.company?.id || position.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesStation && matchesCompany;
    }).sort((a, b) => {
      switch (sortOrder) {
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'id_desc':
          return b.id - a.id;
        case 'id_asc':
          return a.id - b.id;
        case 'name_asc':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [positions, searchTerm, stationFilter, sortOrder, companyFilterValues]);

  const totalItems = filteredPositions.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedPositions = filteredPositions.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'position',
      header: 'Cargo',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: position => (
        <>
          <div className="manager-table-avatar">
            <FiLayers size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {position.name}
            </span>
            <span className="manager-table-subtitle">
              ID: {position.id}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'scope',
      header: 'Vinculação',
      width: '1.8fr',
      cellClassName: 'manager-table-cell',
      render: position => {
        const stations = position.serviceStations || [];
        // Check legacy ID just in case
        const legacyId = position.service_station_id;

        if (stations.length === 0) {
          if (legacyId) {
             // If we have a legacy ID but no stations linked yet, show hint
             return (
               <div className="manager-table-cnpj">
                  <FiMapPin size={14} />
                  <span>Posto ID: {legacyId} (Migrar)</span>
               </div>
             );
          }
          return (
            <span className="manager-table-subtitle">
              Cargo geral da empresa
            </span>
          );
        }

        if (stations.length === 1) {
             const station = stations[0];
             return (
              <div className="manager-table-cnpj">
                <FiMapPin size={14} />
                <span>
                  {station.nickname || station.name}
                </span>
              </div>
            );
        }

        return (
          <div className="manager-table-cnpj">
            <FiMapPin size={14} />
            <span title={stations.map(s => s.nickname || s.name).join(', ')}>
              {stations.length} postos vinculados
            </span>
          </div>
        );
      },
    },
    {
      id: 'cbo',
      header: 'CBO',
      width: '1.2fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: position => (
        <div className="manager-table-cnpj">
          <FiHash size={14} />
          <span>{position.cbo || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: position => {
        const companyName = position.company ? (position.company.corporate_name || position.company.trade_name) : 'N/A';
        return <span className="manager-table-text">{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: position => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(position);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDelete(position);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function PositionManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <PositionManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiLayers size={22} />
                </div>
                <div>
                  <h1>Cargos</h1>
                  <p>
                    Visualize, filtre e gerencie todos os cargos da empresa selecionada.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {permissions.canCreate && (
                  <>
                    <Button 
                      onClick={() => setIsImportModalOpen(true)} 
                      className="manager-header-button" 
                      variant="secondary"
                    >
                      <FiUpload size={18} />
                      Importar
                    </Button>
                    <Button onClick={handleExport} className="manager-header-button" variant="secondary">
                        <FiDownload size={18} />
                        Exportar
                    </Button>
                    <Button onClick={handleCreate} className="manager-header-button">
                        <FiPlus size={18} />
                        Novo Cargo
                    </Button>
                  </>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {positions.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredPositions.length} de {positions.length} cargos
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                    label="Empresa"
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="position-search"
                      placeholder="Busque por nome, descrição ou CBO"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <Select
                    id="station-filter"
                    label="Filtrar por Posto"
                    name="stationFilter"
                    value={stationFilter}
                    onChange={e => setStationFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'Todos' },
                      { value: 'general', label: 'Somente Gerais (Sem vínculo)' },
                      ...stations.map(s => ({
                        value: String(s.id),
                        label: s.nickname || s.name,
                      })),
                    ]}
                    useCustomDropdown={true}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <Select
                    label="Ordenação"
                    name="sortOrder"
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    options={[
                      { value: 'name_asc', label: 'Nome (A-Z)' },
                      { value: 'name_desc', label: 'Nome (Z-A)' },
                      { value: 'id_desc', label: 'Mais recentes' },
                      { value: 'id_asc', label: 'Mais antigos' },
                    ]}
                  />
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && positions.length === 0 && (
              <div className="empty-state">
                <p>Nenhum cargo cadastrado.</p>
              </div>
            )}

            {!loading && !error && positions.length > 0 && (
              <Table
                items={paginatedPositions}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={position => position.id}
                emptyMessage="Nenhum cargo encontrado com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedPosition ? 'Editar Cargo' : 'Novo Cargo'}
        closeOnOverlayClick={false}
      >
        <PositionForm
          companyId={companyFilter !== 'all' ? companyFilter : null}
          companies={companies}
          initialData={selectedPosition}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <PositionImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => loadPositions()}
        companyId={companyFilter !== 'all' ? companyFilter : null}
        companies={companies}
      />

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o cargo{' '}
            {positionToDelete && (
              <span className="delete-modal-company-name">
                {positionToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}

